import { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import type { TWidget } from 'utility/types/dataProvider';
import { independentSystemWidgetIds } from 'screens/dashboard/constants';
import { wavingHandEmoji } from 'utility/constants';
import { getRefreshIndicator } from '../shared/utils';
import { useWidgetContext } from '../Widget';
import WidgetHeading from './WidgetHeading';
import WidgetLoadingChip from './WidgetLoadingChip';
import WidgetActions from './WidgetActions';
import WidgetHelperText from './WidgetHelperText';

// Utility function for title formatting with special cases for system widgets.
export const getWidgetTitle = (
  widget: TWidget,
  t: (key: string) => string,
  isIndependentSystemWidget?: boolean,
) => {
  const isSystemWidget: boolean = widget?.type === 'system';

  // Special case: 'help' module gets localized header + emoji
  if (isSystemWidget && widget?.module?.id === 'help') {
    const strNeedHelp = t('blox.dashboards.needHelp.header');
    const strNeedHelpSubtitle = t('blox.dashboards.needHelp.subtitle');

    return (
      <span>
        {strNeedHelp}&nbsp;{wavingHandEmoji}&nbsp;
        <Typography
          variant="subtitle2"
          component="span"
        >
          {strNeedHelpSubtitle}
        </Typography>
      </span>
    );
  }

  // Special case: 'status' module gets hardcoded title
  if (isIndependentSystemWidget && widget?.id === independentSystemWidgetIds.status) {
    return 'Service Updates';
  }

  return widget?.title;
};

export type WidgetHeaderProps = {
  title: string | React.ReactNode;
  testIdPrefix: 'kpi' | 'widget-header';
  className?: string;
  dateRange?: number;
  headingSx?: SystemStyleObject;
  allowExpand: boolean;
  isHidden: boolean;
  hideAlertButton?: boolean;
  setIsExpanded?: React.Dispatch<React.SetStateAction<boolean>>;
  renderLoadingChip?: boolean;
};

const WidgetHeader = ({
  title,
  testIdPrefix,
  className,
  dateRange,
  headingSx,
  allowExpand,
  isHidden,
  hideAlertButton,
  setIsExpanded,
  renderLoadingChip = true,
}: WidgetHeaderProps) => {
  const {
    widget,
    chartType,
    isLiveUpdating,
    setIsLiveUpdating,
    currentLoadingChipStatus,
    setCurrentLoadingChipStatus,
  } = useWidgetContext();

  const { type, refresh, isLive } = useMemo(() => getRefreshIndicator(widget), [widget]);
  const isKpi = chartType === 'kpi';

  return (
    <Box
      data-testid={
        testIdPrefix === 'kpi'
          ? `kpi-${widget?.module?.id}`
          : `widget-header--${widget?.id}`
      }
      className={className}
      sx={{
        display: 'flex',
        alignItems: isKpi ? 'start' : 'center',
        justifyContent: 'space-between',
      }}
    >
      <Stack
        spacing={1}
        data-testid={testIdPrefix === 'kpi' ? 'kpi-header' : undefined}
        sx={{
          alignItems: isKpi ? 'start' : 'center',
          flexDirection: 'row',
          justifyContent: 'flex-start',
        }}
      >
        <Stack
          data-testid={testIdPrefix === 'kpi' ? 'kpi-title' : undefined}
          sx={{
            alignItems: 'start',
            flexDirection: 'row',
            gap: 1,
          }}
        >
          <WidgetHeading sx={headingSx}>{title}</WidgetHeading>
        </Stack>

        {/* Date-range label (KPI only). */}
        {!!dateRange && (
          <Typography
            variant="subtitle2"
            data-testid="kpi-date-range"
            sx={{
              textAlign: 'end',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            {`Last ${dateRange} days`}
          </Typography>
        )}

        {/* Live Loading Chip. */}
        {renderLoadingChip && (
          <WidgetLoadingChip
            type={type}
            refresh={refresh}
            isLive={isLive}
            isLiveUpdating={isLiveUpdating}
            setIsLiveUpdating={setIsLiveUpdating}
            currentLoadingChipStatus={currentLoadingChipStatus}
            setCurrentLoadingChipStatus={setCurrentLoadingChipStatus}
            isRendered={renderLoadingChip}
          />
        )}

        <WidgetHelperText />
      </Stack>

      {/* Actions: export csv, expand view, etc. */}
      {chartType !== null && (
        <WidgetActions
          allowExpand={allowExpand}
          setIsExpanded={setIsExpanded}
          isHidden={isHidden}
          hideAlertButton={hideAlertButton}
        />
      )}
    </Box>
  );
};

export default WidgetHeader;
